import React from 'react'

import { connect } from 'react-redux'
import styled from 'styled-components'

import { postitActions } from '@services'

import { bindActionToPromise } from '@helpers'

import type { DebounceLoadConfig } from '@hooks'
import { useDebouncedLoadData } from '@hooks'

import type { OnOpenChangeCallbackProps } from '@components/ui/Popover'
import { InteractiveTooltip } from '@components/ui/Tooltips'

import { StatusIconWrapper } from '../StatusIconWrapper'
import { ChatBubbleIcon } from '../svgIcons'
import type { PostItHistoryData } from './AsyncPostitHistory'
import { AsyncPostitHistory } from './AsyncPostitHistory'

const Count = styled.span`
  position: absolute;
  color: ${({ theme }) => theme.colors.gray[102]};
  font-size: 10px;
  font-weight: 700;
  line-height: 1.6;
  width: 100%;
  text-align: center;
`

const StyledStatusIconWrapper = styled(StatusIconWrapper)`
  position: relative;
`

const LOAD_CONFIG: DebounceLoadConfig = { loadBehavior: 'firstOpen' }

interface InvoicePostItStatusProps {
  count: number
  documentType: 'expense' | 'income'
  invoiceId: ItemIdType
  loadData: AsyncFunction<{ documentType: 'expense' | 'income'; documentId: ItemIdType }, PostItHistoryData[]>
}

function PureInvoicePostItStatus({ invoiceId, count, documentType, loadData }: InvoicePostItStatusProps) {
  const asyncLoadData = React.useCallback(
    async () => await loadData({ documentType, documentId: invoiceId }),
    [documentType, invoiceId, loadData]
  )

  const { fetched, loading, results, handleOpen, handleClose } = useDebouncedLoadData(asyncLoadData, LOAD_CONFIG)

  function onOpenChangeCallback({ nextOpen }: OnOpenChangeCallbackProps) {
    if (nextOpen) {
      handleOpen()
    } else {
      handleClose()
    }
  }

  return (
    <InteractiveTooltip
      title={<AsyncPostitHistory data={results} fetched={fetched} loading={loading} />}
      onOpenChangeCallback={onOpenChangeCallback}
    >
      <StyledStatusIconWrapper>
        <Count>{count}</Count>
        <ChatBubbleIcon size={20} />
      </StyledStatusIconWrapper>
    </InteractiveTooltip>
  )
}

export const AsyncInvoicePostItStatus = connect(null, dispatch => ({
  loadData: bindActionToPromise(dispatch, postitActions.fetchInvoicePostIts.request),
}))(PureInvoicePostItStatus)

AsyncInvoicePostItStatus.displayName = 'AsyncInvoicePostItStatus'
